import React, { useState, useEffect, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { GraduationCap, Loader2, PlayCircle, Send } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function VideoGuides() {
  const { lang } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [topic, setTopic] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const token = localStorage.getItem("rai_token");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/video-guides"); setGuides(data.guides); if (data.guides[0]) setActive(data.guides[0]); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitRequest = async () => {
    if (!topic.trim()) { toast.error(L("Indica l'argomento", "Enter a topic")); return; }
    setSending(true);
    try {
      await api.post("/video-guides/requests", { topic, note });
      toast.success(L("Richiesta inviata! Creeremo una guida.", "Request sent! We'll create a guide."));
      setTopic(""); setNote("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const cats = [...new Set(guides.map((g) => g.category || "Generale"))];

  return (
    <div className="min-h-screen bg-background">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-1"><GraduationCap className="w-6 h-6 text-primary" /><h1 className="text-2xl font-bold">{L("Video guide", "Video guides")}</h1></div>
        <p className="text-sm text-muted-foreground mb-6">{L("Impara a usare Revenue AI con le video guide.", "Learn to use Revenue AI with video guides.")}</p>

        {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-muted-foreground" /></div> : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Player + list */}
            <div className="lg:col-span-2 space-y-4">
              {active ? (
                <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="video-player">
                  <video key={active.id} controls className="w-full bg-black aspect-video"
                    src={`${BACKEND}/api/video-guides/${active.id}/stream?auth=${token}`} />
                  <div className="p-4">
                    <h2 className="font-semibold text-lg">{active.title}</h2>
                    {active.description && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{active.description}</p>}
                  </div>
                </div>
              ) : <p className="text-sm text-muted-foreground bg-card border border-border rounded-lg p-6" data-testid="guides-empty">{L("Nessuna video guida disponibile al momento.", "No video guides available yet.")}</p>}

              {cats.map((cat) => (
                <div key={cat}>
                  <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">{cat}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {guides.filter((g) => (g.category || "Generale") === cat).map((g) => (
                      <button key={g.id} onClick={() => setActive(g)} data-testid={`guide-item-${g.id}`}
                        className={`text-left rounded-lg border p-3 flex items-center gap-2 ${active?.id === g.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}>
                        <PlayCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-sm font-medium truncate">{g.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Request form */}
            <div className="bg-card border border-border rounded-lg p-5 h-fit" data-testid="guide-request-form">
              <h3 className="font-semibold mb-1">{L("Richiedi una spiegazione", "Request an explanation")}</h3>
              <p className="text-xs text-muted-foreground mb-3">{L("Non trovi quello che cerchi? Chiedici una guida su un argomento specifico.", "Can't find it? Ask us for a guide on a specific topic.")}</p>
              <input value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="guide-request-topic"
                placeholder={L("Argomento (es. come importare i dati)", "Topic (e.g. how to import data)")}
                className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm mb-2" />
              <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="guide-request-note"
                placeholder={L("Dettagli (facoltativo)", "Details (optional)")} rows={4}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm mb-3" />
              <button onClick={submitRequest} disabled={sending} data-testid="guide-request-submit"
                className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{L("Invia richiesta", "Send request")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
